import React, { useState, useEffect, useRef } from 'react';
import { X, CreditCard, Loader } from 'lucide-react';
import { getStripe } from '../../../services/stripeService';
import promotionApi from '../../../services/promotionApi';
import { formatCurrency } from '../../../services/restaurantApi';

const PromotionPaymentModal = ({ promotion, amount, onClose, onSuccess }) => {
  const [stripe, setStripe] = useState(null);
  const [elements, setElements] = useState(null);
  const [paymentElement, setPaymentElement] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [paymentIntent, setPaymentIntent] = useState(null);
  const paymentElementRef = useRef(null);
  const containerRef = useRef(null);

  useEffect(() => {
    const initializeStripe = async () => {
      try {
        const stripeInstance = await getStripe();
        if (!stripeInstance) {
          throw new Error('Échec de l\'initialisation de Stripe');
        }
        setStripe(stripeInstance);
      } catch (err) {
        setError(err.message);
      }
    };

    initializeStripe();
  }, []);

  const needsStripePayment =
    promotion &&
    promotion.requires_payment &&
    promotion.stripe_payment_status !== 'paid';

  const displayAmount =
    paymentIntent && paymentIntent.amount != null
      ? Number(paymentIntent.amount)
      : Number(amount || 0);

  useEffect(() => {
    const createPaymentIntent = async () => {
      if (!needsStripePayment) return;

      try {
        setIsLoading(true);
        const result = await promotionApi.createPromotionPaymentIntent(promotion.id);
        setPaymentIntent(result);
      } catch (err) {
        setError(err.message || 'Erreur lors de la création du PaymentIntent');
      } finally {
        setIsLoading(false);
      }
    };

    if (stripe && needsStripePayment) {
      createPaymentIntent();
    }
  }, [stripe, promotion?.id, needsStripePayment]);

  useEffect(() => {
    if (!stripe || !paymentIntent) return;

    const clientSecret = paymentIntent?.client_secret || paymentIntent?.clientSecret || null;
    if (!clientSecret) {
      setError('Impossible d\'initialiser le paiement: client_secret manquant.');
      return;
    }

    // Nettoyer l'élément existant
    if (paymentElementRef.current) {
      try {
        paymentElementRef.current.unmount();
      } catch (err) {
        // Ignorer les erreurs si l'élément n'existe pas
      }
      paymentElementRef.current = null;
    }

    // Créer le groupe Elements avec clientSecret (obligatoire pour PaymentElement)
    const elementsInstance = stripe.elements({
      clientSecret,
      appearance: {
        theme: 'stripe',
      },
    });
    setElements(elementsInstance);

    // Créer l'élément de paiement
    const paymentElementInstance = elementsInstance.create('payment', {
      layout: 'tabs',
    });

    paymentElementRef.current = paymentElementInstance;

    // Monter l'élément
    if (containerRef.current) {
      paymentElementInstance.mount(containerRef.current);
    }

    return () => {
      if (paymentElementRef.current) {
        try {
          paymentElementRef.current.unmount();
        } catch (err) {
          // Ignorer les erreurs de démontage
        }
        paymentElementRef.current = null;
      }
    };
  }, [elements, paymentIntent]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!stripe || !elements || !paymentElementRef.current || !paymentIntent) {
      setError('Stripe n\'est pas encore initialisé');
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Soumettre les éléments avant confirmPayment (requis par Stripe)
      const { error: submitError } = await elements.submit();
      if (submitError) {
        setError(submitError.message || 'Erreur lors de la soumission du formulaire');
        setIsLoading(false);
        return;
      }

      // 2. Confirmer le paiement
      const { error: confirmError, paymentIntent: confirmedIntent } = await stripe.confirmPayment({
        elements,
        clientSecret: paymentIntent?.client_secret || paymentIntent?.clientSecret,
        confirmParams: {
          return_url: `${window.location.origin}/management/promotions?payment=success`,
        },
        redirect: 'if_required',
      });

      if (confirmError) {
        setError(confirmError.message);
        setIsLoading(false);
        return;
      }

      if (confirmedIntent && confirmedIntent.status === 'succeeded') {
        // Confirmer le paiement côté backend
        await promotionApi.confirmPromotionPayment(promotion.id, confirmedIntent.id);
        
        // Appeler le callback de succès
        onSuccess();
      } else {
        setError('Le paiement n\'a pas pu être confirmé');
        setIsLoading(false);
      }
    } catch (err) {
      setError(err.message || 'Erreur lors du paiement');
      setIsLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md p-6 relative">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <h2 className="text-xl font-bold text-gray-900">Paiement de la promotion</h2>
          <button
            type="button"
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-full transition-colors"
            aria-label="Fermer"
          >
            <X className="w-5 h-5 text-gray-500" />
          </button>
        </div>

        {/* Informations de la promotion */}
        <div className="mb-6 p-4 bg-gray-50 rounded-lg">
          <h3 className="font-semibold text-gray-900 mb-2">{promotion.title}</h3>
          <div className="flex justify-between items-center">
            <span className="text-sm text-gray-600">Montant à payer:</span>
            <span className="text-xl font-bold text-[#054625]">{formatCurrency(amount)}</span>
          </div>
        </div>

        {/* Formulaire de paiement */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {error && (
            <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded">
              {error}
            </div>
          )}

          {isLoading && !paymentIntent && needsStripePayment && (
            <div className="flex items-center justify-center py-8">
              <Loader className="w-6 h-6 animate-spin text-[#054625]" />
              <span className="ml-2 text-gray-600">Initialisation du paiement...</span>
            </div>
          )}

          {paymentIntent && (
            <>
              <div className="border border-gray-200 rounded-lg p-4">
                <div ref={containerRef} id="promotion-payment-element"></div>
              </div>

              <div className="flex space-x-4">
                <button
                  type="button"
                  onClick={onClose}
                  className="flex-1 px-4 py-2 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
                  disabled={isLoading}
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={isLoading || !stripe}
                  className="flex-1 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
                >
                  {isLoading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin mr-2" />
                      Traitement...
                    </>
                  ) : (
                    <>
                      <CreditCard className="w-4 h-4 mr-2" />
                      Payer {formatCurrency(displayAmount)}
                    </>
                  )}
                </button>
              </div>
            </>
          )}
        </form>
      </div>
    </div>
  );
};

export default PromotionPaymentModal;


